// Revenue panel — what the club actually collected.
//
// Rendered as a tab by the Financials page. It owns its own Stripe read and
// its own refresh, because nothing else on that page needs either.
//
// Stripe is the source of truth for money that went through Stripe, and
// Firestore for who and what. Nothing here is computed from the purchases
// mirror, which receives only what the app itself sold and so has never seen
// more than about half the money.
//
// Two things on the page are not payments at all. Court rentals are invoiced
// directly, and the Sales row is inferred from stock bought from Wilson at a
// flat fifty dollars a ball, because the desk takes cash and writes nothing
// down. Both are reported on their own line in the reconciliation below the
// bars, so the Stripe figure stays the one that ties to the bank.
//
// Sales is also the only row carrying a cost of goods, and therefore the only
// one showing a margin. That is not an omission elsewhere: coach pay is the
// cost of running training but it is not the cost OF a training payment, and
// setting the two against each other would invent a margin the club has no
// basis for. Reselling a ball is the one place both halves of the trade are
// known, and the margin is taken against the resold stock alone rather than
// the whole row, which also carries apparel Wilson had no part in.
//
// Court rentals are the exception, and they have to be: the club invoices
// schools and clubs directly, so that money never reaches a processor at all.
// It comes from the club's own records instead, which means it is only as good
// as someone having marked the invoice paid. The two halves are reported apart
// for exactly that reason — the Stripe half can be checked against the bank,
// the invoiced half cannot.
//
// The figures are cash, not accrual — a payment counts on the day it settles,
// which is what the bank shows. An annual membership paid in March is $250 of
// March, not $20.83 a month. That is also why recurring and one-time are shown
// apart rather than added: a month carrying three annual renewals otherwise
// reads as growth that will not repeat.
//
// Two windows rather than one. Year to date says how the year is going; thirty
// days says whether that is still true. Neither is useful without the other.
//
// On the charts: source is a magnitude comparison, so the bars are one hue —
// length already encodes the amount, and stepping colour as well would imply a
// second variable that does not exist. Colour is spent only where there really
// are two categories, on recurring against one-time, and those two slots are
// validated (adjacent CVD ΔE 24.7, normal-vision 33.6, both clear).
import React, { useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, CircularProgress, Alert, Table, TableBody,
  TableCell, TableRow, Tooltip, ToggleButton, ToggleButtonGroup, Link, Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { logError } from '../../utils/errorLogger';

const INK = '#1E3A5F';
const TEXT_2 = '#52514e';
const MUTED = '#8a8880';
const SURFACE = '#fcfcfb';

// Validated categorical slots 1–2 (light, surface #fcfcfb). Used for the one
// genuine two-category split on the page.
const KIND = { recurring: '#2a78d6', oneTime: '#eb6834' };

// One hue for the source bars. See the note at the top of the file.
const BAR = '#2a78d6';

// Rows that are true but are not the club selling something. They stay in the
// total — dropping them would make the page disagree with the bank — but they
// sort last and read quieter, because they are caveats on the rows above them
// rather than parts of the business.
const ASIDE = new Set(['unattributed', 'pushPress']);

const NOTE = {
  pushPress: 'The previous club system, billing through the same Stripe account. It stopped taking payments in April 2026.',
  unattributed: 'Settled money the club’s own records cannot name — almost always a charge raised by hand in the Stripe dashboard.',
};

const money = (n) => `$${Math.round(n).toLocaleString('en-US')}`;

// The shelf price the Sales row is built on. Named here only to say so in
// words; the multiplication itself happens server-side, so this cannot change
// a figure — it can only describe one.
const RESALE = 50;

// What the number in the last column counts. Sales counts balls over the
// counter; every other row counts payments taken. Calling both "payments"
// would make 151 restock invoices out of four.
const tally = (r) => r.units
  ? `${r.units} item${r.units === 1 ? '' : 's'}`
  : `${r.count} payment${r.count === 1 ? '' : 's'}`;

// A list of names, joined the way a person would say them.
const andList = (xs) => xs.length < 2 ? (xs[0] || '')
  : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;

function Section({ children, sx }) {
  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e8e8e4',
      backgroundColor: SURFACE, height: '100%', ...sx }}>
      {children}
    </Paper>
  );
}

/**
 * A window's headline: the total, then how it splits.
 *
 * The split is a stacked bar rather than two numbers alone because the question
 * it answers — is this club living on renewals or on one-off sales — is a
 * proportion, and a proportion is read faster than it is calculated.
 */
function WindowCard({ title, subtitle, data }) {
  const total = data.total || 0;
  const parts = [
    { key: 'recurring', label: 'Recurring', value: data.recurring || 0, color: KIND.recurring },
    { key: 'oneTime', label: 'One-time', value: data.oneTime || 0, color: KIND.oneTime },
  ].filter(p => p.value > 0);

  return (
    <Section>
      <Typography variant="overline" sx={{ color: MUTED, letterSpacing: '0.08em' }}>{title}</Typography>
      <Typography sx={{ fontSize: '2.75rem', lineHeight: 1.1, fontWeight: 700, color: INK,
        fontVariantNumeric: 'tabular-nums' }}>
        {money(total)}
      </Typography>
      <Typography variant="body2" sx={{ color: TEXT_2, mb: 2 }}>
        {subtitle} · {data.payments} payment{data.payments === 1 ? '' : 's'}
      </Typography>

      {total > 0 && (
        <>
          <Box sx={{ display: 'flex', height: 24, gap: '2px', mb: 1.25 }}>
            {parts.map((p, i) => (
              <Tooltip key={p.key} title={`${p.label}: ${money(p.value)}`}>
                <Box sx={{
                  flexGrow: p.value, minWidth: 3, backgroundColor: p.color,
                  borderTopLeftRadius: i === 0 ? 4 : 0, borderBottomLeftRadius: i === 0 ? 4 : 0,
                  borderTopRightRadius: i === parts.length - 1 ? 4 : 0,
                  borderBottomRightRadius: i === parts.length - 1 ? 4 : 0,
                }} />
              </Tooltip>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
            {parts.map(p => (
              <Box key={p.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: p.color, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: TEXT_2 }}>
                  {p.label} <Box component="span" sx={{ fontWeight: 700, color: INK,
                    fontVariantNumeric: 'tabular-nums' }}>{money(p.value)}</Box>
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Section>
  );
}

/**
 * The sources, in a fixed order rather than sorted by size.
 *
 * Sorting by amount would make rows swap places between the two windows, so
 * the same line sits somewhere different depending on which button is pressed.
 * Court renters is the clearest case: one school season puts it second over a
 * year and absent over thirty days. Every bar carries its number, so the
 * comparison the sort would have made is still there to read.
 */
function SourceBars({ sources }) {
  const max = Math.max(1, ...sources.map(s => Math.abs(s.amount)));
  return (
    <Box>
      {sources.map(s => {
        const aside = ASIDE.has(s.key);
        return (
          <Box key={s.key} sx={{ py: 0.85, borderBottom: '1px solid #f0f0ec' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" sx={{
                width: 168, flexShrink: 0, color: aside ? MUTED : INK,
                fontStyle: aside ? 'italic' : 'normal',
              }}>
                {s.label}
              </Typography>
              <Box sx={{ flexGrow: 1, minWidth: 40 }}>
                <Tooltip title={`${s.label}: ${money(s.amount)} over ${tally(s)}`}>
                  <Box sx={{
                    height: 14, borderRadius: '0 4px 4px 0', minWidth: s.amount ? 3 : 0,
                    width: `${(Math.abs(s.amount) / max) * 100}%`,
                    backgroundColor: BAR, opacity: aside ? 0.35 : 1,
                  }} />
                </Tooltip>
              </Box>
              <Typography variant="body2" sx={{
                width: 88, textAlign: 'right', flexShrink: 0, fontWeight: 700,
                color: aside ? MUTED : INK, fontVariantNumeric: 'tabular-nums',
              }}>
                {money(s.amount)}
              </Typography>
              <Typography variant="caption" sx={{
                width: 44, textAlign: 'right', flexShrink: 0, color: MUTED,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {s.units || s.count}
              </Typography>
            </Box>
            {/* Always shown, even at one renter: "Court renters $6,750" does not
                say whether that was a school season or a season of Tuesdays. */}
            {(s.breakdown || []).length > 0 && s.breakdown.map(b => (
              <Box key={b.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 0.4 }}>
                <Typography variant="caption" sx={{ width: 168, flexShrink: 0, color: MUTED, pl: 2 }}>
                  {b.label}
                </Typography>
                <Box sx={{ flexGrow: 1, minWidth: 40 }}>
                  <Tooltip title={`${b.label}: ${money(b.amount)} over ${tally(b)}`}>
                    <Box sx={{
                      height: 8, borderRadius: '0 3px 3px 0', minWidth: b.amount ? 3 : 0,
                      width: `${(Math.abs(b.amount) / max) * 100}%`,
                      backgroundColor: BAR, opacity: 0.45,
                    }} />
                  </Tooltip>
                </Box>
                <Typography variant="caption" sx={{
                  width: 88, textAlign: 'right', flexShrink: 0, color: TEXT_2,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {money(b.amount)}
                </Typography>
                <Typography variant="caption" sx={{
                  width: 44, textAlign: 'right', flexShrink: 0, color: MUTED,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {b.units || b.count}
                </Typography>
              </Box>
            ))}
            {/* Resale is the one line with a cost of goods sitting directly
                under it, so it is the one line where a margin means anything.
                Set as text rather than a bar — see the note above. */}
            {s.cost > 0 && (
              <Typography variant="caption" sx={{ color: MUTED, display: 'block',
                pl: '184px', pt: 0.35 }}>
                {andList(s.vendors || [])} cost {money(s.cost)}
                {s.units > 0 && ` — $${(s.cost / s.units).toFixed(2)} an item against the $${RESALE} it sells for`}
                {'. Margin '}
                <Box component="span" sx={{ fontWeight: 700, color: INK,
                  fontVariantNumeric: 'tabular-nums' }}>
                  {money(s.resale - s.cost)}
                </Box>
                {s.resale > 0 && ` (${Math.round(((s.resale - s.cost) / s.resale) * 100)}%)`}
                {s.resale < s.amount && ' on the resold stock'}.
              </Typography>
            )}
            {aside && NOTE[s.key] && (
              <Typography variant="caption" sx={{ color: MUTED, display: 'block', pl: '184px', pt: 0.25 }}>
                {NOTE[s.key]}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default function RevenuePanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [win, setWin] = useState('yearToDate');
  const [refreshing, setRefreshing] = useState(false);

  // Normally last night's figures, which arrive instantly. Recomputing walks
  // every payment in Stripe and takes half a minute, so it happens only when
  // asked for — after something has just been paid and needs to show.
  const load = async ({ refresh } = {}) => {
    if (refresh) setRefreshing(true);
    setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'collectedRevenue', { timeout: 540000 });
      const res = await fn({ refresh: !!refresh });
      setData(res.data);
    } catch (e) {
      setError(e.message || 'Could not read revenue from Stripe.');
      logError('Financials', e, refresh ? 'collectedRevenue refresh' : 'collectedRevenue');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Mount only. load() is stable in everything that matters and re-running it
    // on every render would mean a Stripe read per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shown = useMemo(() => {
    if (!data) return null;
    const w = data[win];
    if (!w) return null;
    // Asides last however large they grow; the club's own sources by size.
    const sources = [...(w.sources || [])].sort((a, b) =>
      (ASIDE.has(a.key) - ASIDE.has(b.key)) || b.amount - a.amount);
    return { ...w, sources };
  }, [data, win]);

  if (error) {
    return (
      <Box sx={{ maxWidth: 1100 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ maxWidth: 1100 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ color: TEXT_2 }}>
            Reading Stripe — every payment, joined to what it was for. This takes a moment.
          </Typography>
        </Box>
      </Box>
    );
  }

  const asOf = data.asOf ? new Date(data.asOf) : null;
  const ageHours = asOf ? (Date.now() - asOf.getTime()) / 3600000 : Infinity;
  // The nightly job runs at 03:15, so anything past a day and a bit means it
  // has not run. Say so rather than presenting an old number as current.
  const stale = ageHours > 30;
  const asOfText = !asOf ? 'Figures just read from Stripe'
    : ageHours < 1 ? 'Just now, live from Stripe'
    : stale ? `Last updated ${asOf.toLocaleString()} — the nightly refresh may have failed`
    : `Figures from ${asOf.toLocaleString()}`;

  return (
    <Box sx={{ maxWidth: 1100 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Box sx={{ maxWidth: 660 }}>
            <Typography variant="body2" sx={{ color: TEXT_2 }}>
              What the club collected, read from Stripe rather than from anything the app
              wrote down. Cash basis — a payment counts on the day it settled.
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Button size="small" startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
              onClick={() => load({ refresh: true })} disabled={refreshing}
              sx={{ textTransform: 'none' }}>
              {refreshing ? 'Reading Stripe…' : 'Refresh'}
            </Button>
            <Typography variant="caption" sx={{ color: stale ? '#eb6834' : MUTED, display: 'block' }}>
              {asOfText}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <WindowCard title="Year to date" subtitle={`Since 1 January`} data={data.yearToDate} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <WindowCard title="Last 30 days" subtitle="Rolling" data={data.last30Days} />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Section>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: INK }}>Where it came from</Typography>
                  <Typography variant="caption" sx={{ color: MUTED }}>
                    {money(shown.total)} over {shown.payments} payments
                  </Typography>
                </Box>
                <ToggleButtonGroup size="small" exclusive value={win}
                  onChange={(e, v) => v && setWin(v)}>
                  <ToggleButton value="yearToDate" sx={{ textTransform: 'none', px: 1.75 }}>
                    Year to date
                  </ToggleButton>
                  <ToggleButton value="last30Days" sx={{ textTransform: 'none', px: 1.75 }}>
                    Last 30 days
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <SourceBars sources={shown.sources} />

              <Table size="small" sx={{ mt: 2 }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ border: 0, pl: 0, py: 0.3, color: MUTED, fontSize: '0.78rem' }}>
                      Through Stripe
                    </TableCell>
                    <TableCell align="right" sx={{ border: 0, pr: 0, py: 0.3, color: TEXT_2,
                      fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                      {money(shown.stripe || 0)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ border: 0, pl: 0, py: 0.3, color: MUTED, fontSize: '0.78rem' }}>
                      Invoiced directly
                    </TableCell>
                    <TableCell align="right" sx={{ border: 0, pr: 0, py: 0.3, color: TEXT_2,
                      fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                      {money(shown.invoiced || 0)}
                    </TableCell>
                  </TableRow>
                  {(shown.store || 0) > 0 && (
                    <TableRow>
                      <TableCell sx={{ border: 0, pl: 0, py: 0.3, color: MUTED, fontSize: '0.78rem' }}>
                        Store sales (estimated)
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, pr: 0, py: 0.3, color: TEXT_2,
                        fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                        {money(shown.store)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell sx={{ border: 0, pl: 0, py: 0.4, color: TEXT_2,
                      borderTop: '1px solid #e8e8e4' }}>Total collected</TableCell>
                    <TableCell align="right" sx={{ border: 0, pr: 0, py: 0.4, fontWeight: 700,
                      color: INK, fontVariantNumeric: 'tabular-nums', borderTop: '1px solid #e8e8e4' }}>
                      {money(shown.total)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Section>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Section sx={{ backgroundColor: '#f7f7f4' }}>
              <Typography sx={{ fontWeight: 700, color: INK, mb: 1 }}>How to read this</Typography>
              <Typography variant="body2" sx={{ color: TEXT_2, mb: 1 }}>
                The Stripe half is every payment Stripe settled, so it matches the bank by
                construction. A sale the club&rsquo;s records cannot name moves between the
                rows above but never changes that figure — which is why nothing is dropped
                for being hard to classify.
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_2, mb: 1 }}>
                Court rentals are invoiced directly and never touch Stripe, so they come
                from the club&rsquo;s own records and are only as accurate as the invoice
                being marked paid. They are the largest single payments the club takes —
                one high-school season outweighs a year of several other lines — so
                leaving them out would have been the bigger error.
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_2, mb: 1 }}>
                Sales is an estimate, not a record. The desk takes cash for the balls and
                nothing writes the sale down, so the figure is counted from the other side
                of the trade &mdash; every ball bought from Wilson, at the $50 it sells for.
                That counts a case as sold the day it arrived and counts stock still in the
                shed, so it runs ahead of the real number rather than behind it. Giveaway
                stock is excluded. Apparel sold through the app is a real payment and shows
                as its own line under the row.
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_2, mb: 1 }}>
                Because that same expense records what the stock cost, Sales is the one row
                that can show a margin, and it is set against the resold stock only. The
                cost is the invoice, so it is firm; the revenue above it is not, and the
                margin inherits that. Both figures are the whole of the year&rsquo;s buying,
                not of the year&rsquo;s selling. The Wilson invoices themselves stay on the
                Expenses tab, where they count against the club the same as before &mdash;
                nothing here removes them.
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_2, mb: 1 }}>
                This is club-wide, not app-wide. Anything else billing through the same
                Stripe account appears here too, on its own row. Expect this page to read
                higher than any figure taken from the app&rsquo;s own records.
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_2 }}>
                Recurring and one-time are kept apart rather than added, because an annual
                membership lands as one payment. A month with several renewals in it is not
                a month that will repeat.
              </Typography>
              <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 2 }}>
                Rebuilt nightly at 3:15am; Refresh recomputes on the spot.
                Individual payments are on the Purchases tab; membership standing and
                tier mix are under Membership on{' '}
                <Link component={RouterLink} to="/reports" underline="hover">Reports</Link>.
              </Typography>
            </Section>
          </Grid>
        </Grid>
    </Box>
  );
}
